import { firestore } from "./firebase";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
} from "firebase/firestore";
import { Client } from "../@data";
import { hashPassword, verifyPassword } from "../utils/passwordUtils";

const COLLECTION = "clients";

export const getAllClients = async (): Promise<Client[]> => {
  const snapshot = await getDocs(collection(firestore, COLLECTION));
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Client));
};

export const getClientByPhone = async (
  phone: string
): Promise<Client | null> => {
  const q = query(
    collection(firestore, COLLECTION),
    where("phone", "==", phone)
  );
  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  const doc = snapshot.docs[0];
  return { id: doc.id, ...doc.data() } as Client;
};

export const createClient = async (
  data: Omit<Client, "id" | "createdAt">
): Promise<string> => {
  const clientData = {
    ...data,
    createdAt: new Date().getTime(),
  };
  const docRef = await addDoc(collection(firestore, COLLECTION), clientData);
  return docRef.id;
};

export const updateClient = async (id: string, data: Partial<Client>) => {
  const ref = doc(firestore, COLLECTION, id);
  await updateDoc(ref, data);
};

export const deleteClient = async (id: string) => {
  const ref = doc(firestore, COLLECTION, id);
  await deleteDoc(ref);
};

/**
 * Verifica se a senha fornecida está correta para o cliente
 * @param client - Cliente
 * @param password - Senha em texto plano
 * @returns true se a senha está correta
 */
export const verifyClientPassword = (client: Client, password: string): boolean => {
  if (!client.password) {
    return false;
  }
  return verifyPassword(password, client.password);
};

/**
 * Atualiza a senha do cliente
 * @param clientId - ID do cliente
 * @param newPassword - Nova senha em texto plano (string vazia para remover)
 */
export const updateClientPassword = async (clientId: string, newPassword: string) => {
  const ref = doc(firestore, COLLECTION, clientId);
  
  if (newPassword.trim() === "") {
    // Remove a senha
    await updateDoc(ref, { password: null });
  } else {
    // Define nova senha
    const hashedPassword = hashPassword(newPassword);
    await updateDoc(ref, { password: hashedPassword });
  }
};

/**
 * Cria um cliente com senha
 * @param data - Dados do cliente (sem senha)
 * @param password - Senha em texto plano
 * @returns ID do cliente criado
 */
export const createClientWithPassword = async (
  data: Omit<Client, "id" | "createdAt" | "password">,
  password: string
): Promise<string> => {
  const hashedPassword = hashPassword(password);
  const clientData = {
    ...data,
    password: hashedPassword,
    createdAt: new Date().getTime(),
  };
  const docRef = await addDoc(collection(firestore, COLLECTION), clientData);
  return docRef.id;
};
